import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { CheckIcon } from '@heroicons/react/24/outline';
import { offerAPI, subscriptionAPI, mutualisteAPI, formatCurrency, getOfferTypeLabel, getOfferColor, getOfferIcon } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import SubscriptionModal from '../components/Subscription/SubscriptionModal';
import toast from 'react-hot-toast';

const Offers = ({ admin = false }) => {
  const { user } = useAuth();
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const queryClient = useQueryClient();

  // Récupérer les offres
  const { data: offersData, isLoading } = useQuery(
    'offers',
    () => offerAPI.getOffers({ active: true })
  );

  // Récupérer les souscriptions de l'utilisateur
  const { data: subscriptionsData } = useQuery(
    'user-subscriptions',
    () => subscriptionAPI.getSubscriptions(),
    {
      enabled: !!user && user.role === 'MUTUALISTE'
    }
  );

  // Récupérer les données du mutualiste pour vérifier son statut
  const { data: mutualisteData } = useQuery(
    ['mutualiste-profile', user?.id],
    () => mutualisteAPI.getProfile(),
    {
      enabled: !!user && user.role === 'MUTUALISTE'
    }
  );

  const offers = offersData?.data?.offers || [];
  const userSubscriptions = subscriptionsData?.data?.subscriptions || [];

  // Fonction pour vérifier si l'utilisateur peut souscrire à une offre
  const canSubscribeToOffer = (offerId) => {
    if (!user || user.role !== 'MUTUALISTE') {
      return false;
    }

    // Vérifier le statut du mutualiste - doit être "Actif(ve)"
    const mutualiste = mutualisteData?.data;
    const mutualisteStatus = mutualiste?.status?.libelle || mutualiste?.status;
    if (!mutualiste || mutualisteStatus !== 'Actif(ve)') {
      return false;
    }

    // Vérifier s'il y a déjà une souscription active ou en cours pour cette offre
    const existingSubscription = userSubscriptions.find(
      subscription => {
        const status = subscription.status?.libelle || subscription.status;
        return subscription.offerId === offerId && 
               (status === 'Validée' || status === 'En attente' || status === 'En traitement');
      }
    );

    return !existingSubscription;
  };

  // Fonction pour obtenir le message d'impossibilité de souscription
  const getSubscriptionBlockedMessage = (offerId) => {
    if (!user || user.role !== 'MUTUALISTE') {
      return null;
    }

    const mutualiste = mutualisteData?.data;
    const mutualisteStatus = mutualiste?.status?.libelle || mutualiste?.status;
    
    // Vérifier le statut du mutualiste
    if (!mutualiste || mutualisteStatus !== 'Actif(ve)') {
      return {
        type: 'warning',
        message: 'Statut inactif - Contactez l\'administration'
      };
    }

    // Vérifier les souscriptions existantes
    const existingSubscription = userSubscriptions.find(
      subscription => {
        const status = subscription.status?.libelle || subscription.status;
        return subscription.offerId === offerId && 
               (status === 'Validée' || status === 'En attente' || status === 'En traitement');
      }
    );

    if (existingSubscription) {
      return {
        type: 'success',
        message: '✓ Déjà souscrit'
      };
    }

    return null;
  };

  const handleSubscribe = (offer) => {
    if (!canSubscribeToOffer(offer.id)) {
      toast.error('Vous avez déjà une souscription active ou en cours pour ce produit');
      return;
    }

    setSelectedOffer(offer);
    setShowSubscriptionModal(true);
  };

  const handleSubscriptionSuccess = () => {
    // Rafraîchir les données des souscriptions
    queryClient.invalidateQueries('user-subscriptions');
    setSelectedOffer(null);
    setShowSubscriptionModal(false);
  };

  const getOfferIcon = (type) => {
    switch (type) {
      case 'HEALTH_COVERAGE':
        return '🏥';
      case 'SAVINGS':
        return '💰';
      case 'LOAN':
        return '💳';
      default:
        return '📄';
    }
  };

  const getOfferColor = (type) => {
    switch (type) {
      case 'HEALTH_COVERAGE':
        return 'primary';
      case 'SAVINGS':
        return 'secondary';
      case 'LOAN':
        return 'accent';
      default:
        return 'gray';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <h1 className="text-2xl font-bold text-primary-900 mb-2">
          {admin ? 'Gestion des produits' : 'Produits'}
        </h1>
        <p className="text-gray-600">
          {admin 
            ? 'Gérez les produits de la mutuelle'
            : 'Découvrez nos produits et souscrivez à ceux qui vous conviennent'
          }
        </p>
      </div>

      {/* Grille des produits */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {offers.map((offer) => (
          <div key={offer.id} className="card hover:shadow-card-hover transition-shadow">
            {/* En-tête de la carte */}
            <div className={`p-6 rounded-t-xl ${
              offer.type === 'HEALTH_COVERAGE' ? 'bg-gradient-to-r from-blue-50 to-blue-100' :
              offer.type === 'SAVINGS' ? 'bg-gradient-to-r from-green-50 to-green-100' :
              offer.type === 'LOAN' ? 'bg-gradient-to-r from-orange-50 to-orange-100' :
              'bg-gradient-to-r from-gray-50 to-gray-100'
            }`}>
              <div className="flex items-center space-x-3 mb-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-2xl ${
                  offer.type === 'HEALTH_COVERAGE' ? 'bg-blue-200' :
                  offer.type === 'SAVINGS' ? 'bg-green-200' :
                  offer.type === 'LOAN' ? 'bg-orange-200' :
                  'bg-gray-200'
                }`}>
                  {getOfferIcon(offer.type)}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {offer.name}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {getOfferTypeLabel(offer.type)}
                  </p>
                </div>
              </div>
              
              <div className="text-center">
                <p className="text-3xl font-bold text-primary-900">
                  {formatCurrency(offer.price)}
                </p>
                <p className="text-sm text-gray-600">
                  {offer.type === 'LOAN' ? 'Montant maximum' : 
                   offer.type === 'SAVINGS' ? 'minimum par mois' : 'Par mois'}
                </p>
              </div>
            </div>

            {/* Contenu de la carte */}
            <div className="p-6">
              <p className="text-gray-600 mb-6">
                {offer.description}
              </p>

              {/* Fonctionnalités */}
              <div className="space-y-3 mb-6">
                {offer.features?.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-2">
                    <CheckIcon className="w-5 h-5 text-secondary-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="space-y-3">
                {/* Bouton Souscrire/Demander - affiché seulement si l'utilisateur peut souscrire */}
                {canSubscribeToOffer(offer.id) && (
                  <button
                    onClick={() => handleSubscribe(offer)}
                    className="w-full btn-primary"
                  >
                    {offer.name && offer.name.toUpperCase().includes('FSR') ? 'Demander' : 'Souscrire'}
                  </button>
                )}
                
                {/* Messages d'impossibilité de souscription */}
                {user?.role === 'MUTUALISTE' && !canSubscribeToOffer(offer.id) && (() => {
                  const blockedMessage = getSubscriptionBlockedMessage(offer.id);
                  if (!blockedMessage) return null;
                  
                  const bgColor = blockedMessage.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200';
                  const textColor = blockedMessage.type === 'success' ? 'text-green-700' : 'text-yellow-700';
                  
                  return (
                    <div className={`w-full inline-flex items-center justify-center px-4 py-2 ${bgColor} border rounded-lg text-sm font-medium ${textColor}`}>
                      {blockedMessage.message}
                    </div>
                  );
                })()}
                
                {/* Bouton pour les admins */}
                {user?.role === 'ADMIN' && (
                  <button
                    onClick={() => setSelectedOffer(offer)}
                    className="w-full btn-primary"
                  >
                    Voir les détails
                  </button>
                )}
                
                <button className="w-full btn-secondary">
                  En savoir plus
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {offers.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">Aucun produit disponible pour le moment</p>
        </div>
      )}

      {/* Modal de souscription amélioré */}
      <SubscriptionModal
        offer={selectedOffer}
        isOpen={showSubscriptionModal}
        onClose={() => {
          setShowSubscriptionModal(false);
          setSelectedOffer(null);
        }}
        onSuccess={handleSubscriptionSuccess}
      />
    </div>
  );
};

export default Offers;
